/**
 * Both imports are deep paths into side-effect-free leaves, not the `@aline/common`
 * barrel: this theme is loaded by the standalone Teams channel-interaction page, and
 * the barrel would drag the editor/GraphQL graph into that bundle.
 */
import {
  AI_INSERT_HIGHLIGHT_COLOR,
  insertTableCellColor,
  removeTableCellColor,
} from '@aline/common/rendering/colors';
import {
  INITIAL_USER_DELETE_COLOR,
  INITIAL_USER_EDIT_COLOR,
  linkColor,
} from '@aline/common/util/theme-colors';
import { adaptV4Theme, createTheme } from '@mui/material/styles';
import type {
  CSSProperties,
  DeprecatedThemeOptions,
  Theme,
  ThemeOptions,
} from '@mui/material/styles';
import type { DeepPartial } from 'ts-essentials';

enum Colors {
  RULE = 'rgb(209, 209, 209)',
}

interface FontVariant {
  color: CSSProperties['color'];
  fontFamily: CSSProperties['fontFamily'];
  fontSize: CSSProperties['fontSize'];
  fontStretch: CSSProperties['fontStretch'];
  fontStyle: CSSProperties['fontStyle'];
  fontWeight: CSSProperties['fontWeight'];
  letterSpacing: CSSProperties['letterSpacing'];
  lineHeight: CSSProperties['lineHeight'];
}
interface AlineTheme {
  appBorders: {
    outline: string;
  };
  appColors: {
    accent1: string;
    accent2: string;
    accent3: string;
    accent4: string;
    accent5: string;
    accent6: string;
    acceptSuggestion: string;
    avatarLime: string;
    avatarOrange: string;
    avatarPurple: string;
    avatarTeal: string;
    background: string;
    background2: string;
    buttonDisabled: string;
    buttonHover: string;
    buttonPressed: string;
    confirmedGreen: string;
    connectedCheck: string;
    darkBackground: string;
    defaultFieldBackground: string;
    dynamicColors: string[];
    fieldHeader: string;
    halfPrimary: string;
    hoverBackground: string;
    hoverBackgroundSubtle: string;
    iconsType: string;
    iconsTypeMuted: string;
    keepSuggestions: string;
    link: string;
    missingMatchesChipBackground: string;
    missingMatchesChipText: string;
    modalBackground: string;
    partyColor: string;
    placeholderType: string;
    primary: string;
    reddish: string;
    rejectedRed: string;
    rejectSuggestion: string;
    relationshipDirectionChipBackground: string;
    relationshipDirectionChipText: string;
    relationshipReviewChipBackground: string;
    rule: string;
    rulerMargin: string;
    ruleSubtle: string;
    secondaryType: string;
    selectedBackground: string;
    selectedBackgroundNav: string;
    signedGreen: string;
    strongType: string;
    suggestInsert: string;
    suggestInsertBackground: string;
    suggestInsertTableBackground: string;
    suggestRemove: string;
    suggestRemoveBackground: string;
    suggestRemoveTableBackground: string;
    textHighlight: string;
  };
  appShadows: {
    banner: string;
    page: string;
    popper: string;
  };
  appSizes: {
    appBarHeight: number;
    iconHeight: number;
    iconWidth: number;
    inputHeight: number;
  };
  appTypography: {
    buttonSmall: FontVariant;
    defaultFontColor: string;
    defaultFontSize: number;
    defaultHighlightColor: string;
    fieldLabel: FontVariant;
    placeholder: FontVariant;
  };
  appZIndex: {
    caret: number;
    footer: number;
    header: number;
    imageResizer: number;
    scrollbar: number;
  };
  dialogStyles: {
    borderRadius: number;
    headerHeight: number;
    largeDialogWidth: number;
    mediumDialogHeight: number;
    mediumDialogWidth: number;
    smallDialogWidth: number;
    standardDialogWidth: number;
  };
  minimalStorableListRow: {
    rowHeightPx: number;
  };
  navStyles: {
    logoContainerHeight: number;
  };
}

declare module '@mui/material/styles' {
  interface Theme extends AlineTheme {
    __typename: string;
  }
  interface ThemeOptions extends DeepPartial<AlineTheme> {
    __typename?: string;
  }
}

export const hoverButtonOpacity = 0.06;

const appColorsWithoutDynamic: Omit<Theme['appColors'], 'dynamicColors'> = {
  accent1: 'rgb(57, 66, 183)',
  accent2: 'rgb(0, 134, 106)',
  accent3: 'rgb(203, 62, 109)',
  accent4: 'rgb(249, 203, 64)',
  accent5: 'rgb(250, 113, 91)',
  accent6: 'rgb(203, 62, 62)',
  acceptSuggestion: 'rgb(0, 134, 106)',
  avatarLime: 'rgb(0, 135, 14)',
  avatarOrange: 'rgb(185, 90, 49)',
  avatarPurple: 'rgb(131, 63, 204)',
  avatarTeal: 'rgb(0, 106, 135)',
  background: 'rgb(248, 249, 250)',
  background2: 'rgb(249, 249, 249)',
  buttonDisabled: 'rgba(0, 0, 0, 0.5)',
  buttonHover: 'rgb(17, 91, 190)' /** #115BBE */,
  buttonPressed: 'rgba(57, 66, 183, 0.2)',
  confirmedGreen: '#0EB90A',
  connectedCheck: '#00954D',
  darkBackground: 'rgba(50, 74, 82, 0.05)',
  defaultFieldBackground: '#f4f5f5',
  fieldHeader: 'rgb(240, 240, 240)',
  halfPrimary: 'rgba(20, 110, 232, 0.5)',
  hoverBackground: `rgba(20, 110, 232, ${hoverButtonOpacity})`,
  hoverBackgroundSubtle: 'rgba(20, 110, 232, 0.05)',
  iconsType: 'rgb(50, 74, 82)',
  iconsTypeMuted: '#849297',
  keepSuggestions: 'rgb(20, 110, 232)',
  link: linkColor,
  missingMatchesChipBackground: 'rgba(250, 113, 91, 0.15)',
  missingMatchesChipText: 'rgb(250, 113, 91)',
  modalBackground: 'rgba(24, 35, 39, 0.5)',
  partyColor: '#3E88EC',
  placeholderType: 'rgba(118, 118, 118, 0.6)',
  primary: 'rgb(20, 110, 232)',
  reddish: 'rgb(203, 62, 62)',
  rejectSuggestion: 'rgb(203, 62, 62)',
  rejectedRed: '#FC4059',
  relationshipDirectionChipBackground: '#E2E2E2',
  relationshipDirectionChipText: '#44403C',
  relationshipReviewChipBackground: 'rgba(194, 218, 249, 0.5)',
  rule: Colors.RULE,
  ruleSubtle: '#E2E8F0',
  rulerMargin: 'rgb(239, 239, 239)',
  secondaryType: 'rgb(118, 118, 118)',
  selectedBackground: 'rgba(20, 110, 232, 0.15)',
  selectedBackgroundNav: 'rgba(20, 110, 232, 0.1)',
  signedGreen: '#6FCF97',
  strongType: '#111827',
  suggestInsert: INITIAL_USER_EDIT_COLOR,
  suggestInsertBackground: AI_INSERT_HIGHLIGHT_COLOR,
  suggestInsertTableBackground: insertTableCellColor.toString(),
  suggestRemove: INITIAL_USER_DELETE_COLOR,
  suggestRemoveBackground: 'rgba(203, 62, 62, 0.1)',
  suggestRemoveTableBackground: removeTableCellColor.toString(),
  textHighlight: 'rgb(202, 231, 255)',
};
export const appColors: Theme['appColors'] = {
  ...appColorsWithoutDynamic,
  dynamicColors: [
    appColorsWithoutDynamic.primary,
    appColorsWithoutDynamic.accent1,
    appColorsWithoutDynamic.accent2,
    appColorsWithoutDynamic.accent3,
    appColorsWithoutDynamic.accent4,
    appColorsWithoutDynamic.accent5,
    appColorsWithoutDynamic.accent6,
  ],
};
export const roleAvatarColor = appColors.iconsType;

export const alineDefaultFontFamily = '"Open Sans", sans-serif';
const themeOpts: ThemeOptions & DeprecatedThemeOptions = {
  appBorders: {
    outline: `1px solid ${Colors.RULE}`,
  },
  appColors,
  appShadows: {
    banner: '0px -1px 3px 0px #00000026',
    page: '0 .125rem 0.25rem rgba(50, 74, 82, 0.32)',
    popper: '0 .25rem .25rem rgba(50, 74, 82, 0.32)',
  },
  appSizes: {
    appBarHeight: 52,
    iconHeight: 24,
    iconWidth: 24,
    inputHeight: 36,
  },
  appTypography: {
    buttonSmall: {
      color: appColors.primary,
      fontFamily: alineDefaultFontFamily,
      fontSize: '.75rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 'bold',
      letterSpacing: '0.2px',
      lineHeight: 1.33,
    },
    defaultFontColor: 'rgb(0,0,0)',
    defaultFontSize: 12,
    defaultHighlightColor: 'hsla(0, 0%, 0%, 0)',
    fieldLabel: {
      color: appColors.iconsType,
      fontFamily: alineDefaultFontFamily,
      fontSize: '.75rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 600,
      letterSpacing: 'normal',
      lineHeight: 1.5,
    },
    placeholder: {
      color: appColors.placeholderType,
      fontFamily: alineDefaultFontFamily,
      fontSize: '.75rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
  },
  appZIndex: {
    footer: 50,
    header: 50,
    imageResizer: 60,
    /**
     * Above every in-page layer (appBar 1100, drawer 1200) but below MUI's modal layer (1300):
     * overlay scrollbars must never paint over dialogs, menus or popovers.
     */
    scrollbar: 1250,
  },
  dialogStyles: {
    borderRadius: 20,
    headerHeight: 24,
    largeDialogWidth: 900,
    mediumDialogHeight: 900,
    mediumDialogWidth: 712,
    smallDialogWidth: 348,
    standardDialogWidth: 566,
  },
  minimalStorableListRow: {
    rowHeightPx: 48,
  },
  navStyles: {
    logoContainerHeight: 56,
  },
  overrides: {
    MuiDialogActions: {
      root: {
        display: 'flex',
        padding: '24px 0 0 0',
      },
    },
    MuiDialogContent: {
      root: {
        '&:first-child': { paddingTop: 0 },
        display: 'flex',
        padding: 0,
      },
    },
    MuiIconButton: {
      colorPrimary: {
        '&:active': {
          color: appColors.primary,
        },
        '&:hover': {
          backgroundColor: appColors.hoverBackground,
        },
        color: appColors.iconsType,
      },
    },
    MuiTableRow: {
      root: {
        '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: appColors.selectedBackground },
        '&:hover': { backgroundColor: appColors.hoverBackground, cursor: 'pointer' },
      },
      selected: {},
    },
  },

  palette: {
    action: {
      hover: appColors.hoverBackground,
      hoverOpacity: 0.06,
      selected: appColors.selectedBackground,
    },
    divider: appColors.rule,
    error: {
      main: appColors.accent6,
    },
    info: {
      main: appColors.accent1,
    },
    primary: {
      dark: appColors.buttonHover,
      main: appColors.primary,
    },
    secondary: {
      main: appColors.accent2,
    },
    success: {
      main: appColors.accent2,
    },
    text: {
      primary: appColors.iconsType,
      secondary: appColors.secondaryType,
    },
    warning: {
      main: appColors.accent4,
    },
  },
  // We can override default props here if we need to, globally.
  props: {
    MuiButton: {
      disableFocusRipple: true,
      disableRipple: true,
    },
    MuiButtonBase: {
      disableRipple: true,
      disableTouchRipple: true,
    },
    MuiButtonGroup: {
      disableFocusRipple: true,
      disableRipple: true,
    },
    MuiCheckbox: {
      disableRipple: true,
    },
    MuiIconButton: {
      color: 'primary',
    },
    MuiRadio: {
      color: 'primary',
    },
  },
  typography: {
    body1: {
      color: appColors.iconsType,
      fontFamily: alineDefaultFontFamily,
      fontSize: '.875rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 1.5,
    },
    button: {
      color: appColors.iconsType,
      fontFamily: alineDefaultFontFamily,
      fontSize: '.875rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 600,
      letterSpacing: 'normal',
      lineHeight: 1.43,
    },
    caption: {
      color: appColors.iconsType,
      fontFamily: alineDefaultFontFamily,
      fontSize: '.75rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 1.5,
    },
    fontFamily: alineDefaultFontFamily,
    h1: {
      color: appColors.iconsType,
      fontFamily: alineDefaultFontFamily,
      fontSize: '1.5rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 600,
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    h2: {
      color: appColors.iconsType,
      fontFamily: alineDefaultFontFamily,
      fontSize: '1.25rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 600,
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    subtitle1: {
      color: appColors.iconsType,
      fontFamily: alineDefaultFontFamily,
      fontSize: '1rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 'bold',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    subtitle2: {
      color: appColors.iconsType,
      fontFamily: alineDefaultFontFamily,
      fontSize: '.875rem',
      fontStretch: 'normal',
      fontStyle: 'normal',
      fontWeight: 600,
      letterSpacing: 'normal',
      lineHeight: 1.29,
    },
  },
};

// The theme can be used to override styles or props on components application-wide.
export const themeV5 = createTheme(adaptV4Theme(themeOpts), {
  components: {
    /**
     * Icons are almost always flex children sitting next to text, and the flex
     * default (`flex-shrink: 1`) lets a long label squash the icon into a
     * distorted sliver instead of wrapping or truncating the text. Default every
     * SVG to not shrinking. This is an element selector, so any class-based rule
     * (`flexShrink: 1` on a genuinely fluid SVG, e.g. a chart that should track
     * its container) still wins on specificity.
     */
    MuiCssBaseline: {
      styleOverrides: {
        svg: {
          flexShrink: 0,
        },
      },
    },
    /**
     * MUI v9 dropped `inherit` from the Typography color variants list, so
     * `<Typography color="inherit">` no longer emits `color: inherit` CSS.
     * Without it, Aline's `theme.typography.body1.color` (set on this theme
     * for default text appearance) wins, breaking cases where Typography is
     * inside a colored parent (e.g. the Send button label inside a contained-
     * primary AlineButton). Add the variant back so legacy callsites work.
     */
    MuiTypography: {
      variants: [
        {
          props: { color: 'inherit' },
          style: { color: 'inherit' },
        },
      ],
    },
  },
});

export const alineLinkStyles = {
  color: appColors.link,
  fontFamily: alineDefaultFontFamily,
  fontSize: '.75rem',
  fontStretch: 'normal',
  fontStyle: 'normal',
  fontWeight: 400,
  letterSpacing: 'normal',
  lineHeight: 1.5,
  textDecoration: 'none',
};
export const alineLinkStylesWithUnderline = {
  ...alineLinkStyles,
  '&:hover': {
    textDecoration: 'underline',
  },
};
export const alineLinkButtonStyles = {
  ...alineLinkStyles,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
};
export const alineLinkButtonStylesWithUnderline = {
  ...alineLinkButtonStyles,
  '&:hover': {
    textDecoration: 'underline',
  },
};
export const alineLinkButtonStylesWithUnderlineNotBold = {
  ...alineLinkButtonStyles,
  '&:hover': {
    textDecoration: 'underline',
  },
  fontWeight: 400,
};

// Common Hardcoded Classnames
export const displayOnHover = 'displayOnHover';

type RemoveIndex<T> = {
  [K in keyof T as string extends K ? never : number extends K ? never : K]: T[K];
};

/**
 * Material-UI's createTheme() adds '@font-face' properties to typography variants,
 * which are incompatible with the sx prop's type system (SystemStyleObject).
 * This utility strips those properties so typography styles can be safely spread into sx props.
 *
 * Use this when spreading typography styles into sx props:
 * @example
 * <Box sx={getTypographyForSx(theme.typography.caption)} />
 * or
 * <Box sx={{ ...getTypographyForSx(theme.typography.caption), color: 'red' }} />
 */
export function getTypographyForSx(
  typographyStyle: CSSProperties
): RemoveIndex<Omit<CSSProperties, '@font-face'>> {
  const { '@font-face': _fontFace, ...rest } = typographyStyle;
  return rest;
}

export function important<T>(value: T): T {
  return `${value} !important` as unknown as T;
}

/**
 * Clamps lines and auto-resizes with layout changes.
 * Returns a plain object (not CSSProperties) so it can be spread
 * into both MUI sx props and tss-react makeStyles without type errors.
 */
export function createClampStyles(numLines: number) {
  return {
    WebkitBoxOrient: 'vertical' as const,
    WebkitLineClamp: numLines,
    display: '-webkit-box' as const,
    overflow: 'hidden' as const,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  };
}
